import React from 'react';
import { Empty, Table, TableProps } from 'antd';
import { getlogsColumns } from './columns/logsColumns';
import classes from './LogsTable.module.scss';
import { IColumn, ILogs, IPreparedLogs } from 'interfaces';
import TableLoader from 'components/Sceletons/TableLoader';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { useTranslation } from 'react-i18next';
import { EyeOutlined } from '@ant-design/icons';
import { LogsDetailsModal } from 'components/Modals';
import { setIsLogsDetailsOpen } from 'store/slices/dataRoomSlice';
import { setChoosenLog, setLogsForPrint } from 'store/slices/dataLogsSlice';
import { getMockActions } from 'helpers/getMockActions';
import en from 'dayjs/locale/en';
import { defaultGroups } from 'store/reducers/usersGroupCreator';
import DotIcon from './DotIcon';
import cn from 'classnames';
import ResizeObserver from "rc-resize-observer";

type LogsTableProps = {
  logs: any[];
  isLoading: boolean;
  scroll: { x?: string | number, y?: string | number };
  height?: string;
  emptyHeight?: string;
  virtual?: boolean;
  isUdating?: boolean;
}

export default function LogsTable({ logs, isLoading, scroll, height, emptyHeight, virtual, isUdating } : LogsTableProps) {
  const [modifiedColumns, setModifiedColumns] = React.useState<IColumn[]>([]);  
  const [isTableResizing, setIsTableResizing] = React.useState(false);
  const resizeTimeoutRef = React.useRef<NodeJS.Timeout>();

  const { isLogsDetailsOpen } = useAppSelector(store => store.dataRoom);
  const { userData } = useAppSelector(store => store.userData);
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();

  const staticRuT = React.useMemo(() => i18n.getFixedT('ru'), []);
  const mockActions = React.useMemo(() => getMockActions(staticRuT, t), [i18n.language]);

  React.useEffect(()=> {
    setModifiedColumns(getActualFilters(logs));
  },[logs, i18n.language]);


  const renderCell = (props: any) => {
    const { value, info, col } = props;
    if ( col.key === 'details') {
      return <div
        className={classes.centredCell}
        onClick={() => openLogDetails(info)}
      >
        <EyeOutlined />
      </div>
    }

    if ( col.key === 'time') {      
      return value && userData?.date_format === 'international'
        ? info.timestamp.format('HH:mm:ss')
        : info.timestamp.locale(en).format('hh:mm a')
    }

    if ( col.key === 'date') {      
      const time = value && userData?.date_format === 'international'
        ? info.timestamp.format('HH:mm:ss')
        : info.timestamp.locale(en).format('hh:mm a')

      const today = info.timestamp?.isToday()

      return (
        <div className={classes.timeBlock}>
          <div className={cn(classes.timeWrap, today ? classes.primary : classes.grey)}>
            <DotIcon width={8} height={8} />
            <div className={classes.create_line} />
          </div>
          <div className={cn(classes.colWrap, classes.dateAndTime)} >
            <div className={classes.date}>{value}</div>
            <div>{time}</div>
          </div>
        </div>
      )
    }

    if(col.key === 'entityname' && value === 'отсутствует'){
      const actionIdx = mockActions.ru.findIndex((action) => value === action);
      return mockActions.actual[actionIdx];
    }

    if (col.key === 'username') {
      const nameOfRole = defaultGroups.includes(info.role)
        ? t(`Users.table.${info.role}`)
        : info.role;
      
      return <div className={classes.colWrap}>
        <div className={classes.role}>{nameOfRole}</div>
        <div>{value}</div>
      </div>
    }
    return value;
  };

  const openLogDetails = (log: ILogs) => {
    dispatch(setIsLogsDetailsOpen(true));
    dispatch(setChoosenLog(log))
  };

  const getActualFilters = (dataSource: any) => {
    const logsColumns = getlogsColumns(t);

    return logsColumns.map((col: IColumn) => {
      const unicValues = Array.from(new Set(dataSource.map((log: any) => log[col.dataIndex])))
      
      const filteredValues =
        col.dataIndex === 'username' || col.dataIndex === 'email'
          ? unicValues.filter((value: any) => value !== '-')
          : col.dataIndex === 'entityname'
          ? unicValues.filter((value: any) => value !== 'отсутствует')
          : unicValues;

      const currentFilters = filteredValues.map((value: any) => {
        if (col.key === 'action') {
          return { value: value, text: value }
        } else if (value === 'отсутствует') {
          return { value: 'отсутствует', text: t('Logs.none') }
        }
        return { value: value, text: value }
      });      
      return {
        ...col,
        render: (value: any, info: any) => renderCell({value, info, col}),
        filters: (col.dataIndex !== 'time' && col.dataIndex !== 'details') && currentFilters,
        onFilter: (value: boolean, record: any) => record[col.dataIndex] === value,
      }
    })
  }
  
  const onChange: TableProps<object>['onChange'] = (pagination, filters, sorter, extra) => {    
    if (extra.action === 'filter') {
      setModifiedColumns(getActualFilters(extra.currentDataSource));
      dispatch(setLogsForPrint(extra.currentDataSource as IPreparedLogs[]))
    };
  };

  if (isLoading) {   
    return (
      <TableLoader height={height || 'calc(100dvh - 154px)'} borderradius={5}/>
    )
  };

  return (
    <>
      <div className={cn(classes.tableWrapper, { [classes.hidingVirtualScrollbar]: isTableResizing })} style={{height: height || 'calc(100dvh - 154px)'}}>
        <ResizeObserver 
          onResize={({ width }) => {
            setIsTableResizing(true);
            
            if (resizeTimeoutRef.current) {
              clearTimeout(resizeTimeoutRef.current);
            }
            
            resizeTimeoutRef.current = setTimeout(() => {
              setIsTableResizing(false);
            }, 200);
          }}
        >
          <Table
            locale={{emptyText:
              <div style={{height: emptyHeight || 'calc(100dvh - 205px)', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                <Empty
                  description={t('Logs.table.empty')}
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              </div>
            }}
            virtual={virtual}
            columns={modifiedColumns}
            dataSource={logs}
            onRow={(record) => {
              const viewClick = () => openLogDetails(record as ILogs)
              return {
                onClick: viewClick,
              }
            }}
            onChange={onChange}
            scroll={scroll}
            pagination={false}
            size='small'
            className={classes.table}
            loading={isUdating}
          />
        </ResizeObserver>
      </div>

      {isLogsDetailsOpen && (
        <React.Suspense fallback={<div />}>
          <LogsDetailsModal
            open={isLogsDetailsOpen} 
          />
        </React.Suspense>
      )}
    </>
  )
}
